import React, { useMemo, useState, useEffect } from 'react';
import RankTrend from '@/pages/intelligence/pages/TopCharts/PCConsoleGames/components/GamesDetails/StoreRank/RankTrend';
import MarketCount from '@/pages/intelligence/pages/TopCharts/PCConsoleGames/components/GamesDetails/StoreRank/MarketCount';
import type { IBaseProps, IGameType } from '@/pages/intelligence/types/common';
import useMarketFilterApi, {
  MarketSpecailType,
} from '@/pages/intelligence/hooks/useMarketSelector';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import { STORERANK_SEGEMENT } from '@/pages/intelligence/components/Filter/FilterBase/_config';
import useStoreTopRegionOptionsMap from '@intelligence/v3/_hooks/useStoreTopRegionOptionsMap';
import moment from 'moment';
import { DBI_TOPCHARTS } from '@/pages/intelligence/models/topcharts';
import { getSpecialComposedSelectParentOptions } from '@/pages/intelligence/utils/speciaFilterUtils';
import { useDeepCompareEffect } from 'ahooks';
import {
  fetchStoreRanksDetailsTrendData,
  getCombieStoreRanksDetailsRankTrendData,
} from '@/pages/intelligence/services/storeRank';
import { dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { STORERANK_METRICS_OPTIONS_LIST } from '@/pages/intelligence/_config/storeRank/config';
import '@/pages/intelligence/assets/scss/TopCharts/MobileGames/components/StoreRank.scss';
import ExportTable from '@/pages/intelligence/components/ExportTable';
import useRankTrendModel from '@/pages/intelligence/pages/TopCharts/PCConsoleGames/components/GamesDetails/StoreRank/RankTrend/hooks/useRankTrendModel';
import { buriedPointGame } from '@/pages/home/utils/buriedPoint';
import { useSelector } from 'umi';
import useEarliestTime from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/utilsTmp/useEarliestTime';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import {
  DEFAULTFILTERVALUE_COMBINE_RANKTREND as defaultFilterValue,
  FILTERKEYLIST_COMBINE_RANKTREND as filterKeyList,
  DEFAULTINPUTLIST_COMBINE_RANKTREND as defaultInputList,
  COMBINE_STORERANK_RANKTREND_DEFAULT_PARAMS as defaultParams,
} from '@/pages/intelligence/pages/TopCharts/PCConsoleGames/components/GamesDetails/StoreRank/utils/const';
import CommonFilter from '@intelligence/components/CommonFilter';
import useInitFilterHook from '@intelligence/hooks/useInitFilterHook';
import { CONSOLE_PLATFORMS } from '@intelligence/_config/storeRank/config';
import { getCountriesValues } from '@/pages/intelligence/pages/TopCharts/ConsoleGames/components/GamesDetails/StoreRank/utils';
import { handleCombineDetailStoreRankFilterParamsToApi as handleFilterToApiParams } from '@/pages/intelligence/pages/TopCharts/_config/topchartFilterCfg';
import { get } from 'lodash-es';
import {
  COMBINE_ENTITY_TYPE,
  COMBINE_PLATFORMS,
} from '@intelligence/v3/constants/topcharts';
import { getFirstWeekSinceReleaseDates, isFirstWeekSinceReleaseShortcut } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/utils';

interface IStoreRankDetailsProps extends IBaseProps {
  entityType: IGameType;
  gameId: string;
  appIds?: any;
  appIdListAll?: any[];
  isBasicLoading: boolean;
  basicInfo: object | any;
  countryList: any[];
  getRadioOptionsTips?: Function;
}

const StoreRankDetails: React.FC<IStoreRankDetailsProps> = (props) => {
  const {
    entityType = COMBINE_ENTITY_TYPE,
    gameId,
    combineGameIdList = [],
    basicInfo,
    isBasicLoading,
    getRadioOptionsTips,
  } = props;

  // console.log('StoreRankDetails', props);

  const [loading, setLoading] = useState(true); // 用于给segment, loading时禁用， 也可以存在model中
  const [isOperateTime, setIsOperateTime] = useState(false); // 是否操作过时间筛选器，用于给Epic榜单往前推一天的判断
  const urlSearchParams = new URLSearchParams(location.search);
  const urlTopChartsName = decodeURIComponent(
    urlSearchParams.get('source') || '',
  );
  const { detailFilterExtend } = useFilterExtend({});
  const topchartsState = useSelector(
    (state: any) => state?.[DBI_TOPCHARTS] || {},
  );
  if (detailFilterExtend?.rangeTime?.granularity === 'weekly') {
    // 获取当前日期
    const currentDate = moment();

    // 获取当前周的周一日期（如果当前日期不是周一）
    const currentWeekMonday = currentDate.clone().day(1); // 1代表周一
    // 获取四周前的周二日期
    // 首先，找到当前日期所在周的周二
    const thisWeekTuesday = currentDate.clone().day(2); // 2代表周二
    // 然后，回溯到四周前
    const fourWeeksAgoTuesday = thisWeekTuesday.subtract(4, 'weeks');
    detailFilterExtend.rangeTime.end_date =
      currentWeekMonday.format('YYYY-MM-DD');
    detailFilterExtend.rangeTime.start_date =
      fourWeeksAgoTuesday.format('YYYY-MM-DD');
  }

  // 游戏是否已上线
  const isRelease = useMemo(() => {
    return basicInfo?.is_release;
  }, [basicInfo]);

  // 初始化默认分页segment
  const activeSegmentUrlCur = useMemo(() => {
    // if (urlTopChartsName) {
    //   return 'market';
    // }
    return detailFilterExtend?.[STORERANK_SEGEMENT]?.value || 'rank';
  }, [urlTopChartsName, detailFilterExtend]);

  const combineConfig = useMemo(() => {
    const config = {
      hasPc: false,
      hasConsole: false,
    };
    const pcGame = combineGameIdList?.find(
      (item: any) => item.entity_type === 'pc',
    );
    const consoleGame = combineGameIdList?.find(
      (item: any) => item.entity_type === 'console',
    );
    if (pcGame) config.hasPc = true;
    if (consoleGame) config.hasConsole = true;
    return config;
  }, [combineGameIdList]);

  const platformSourceListCur = useMemo(() => {
    return getSpecialComposedSelectParentOptions(combineConfig);
  }, [combineConfig]);

  // 宽表切换segment
  const [rankType, setRankType] = useState('latest_ranking');
  const [earliestPeriodCur, setEarliestPeriodCur] = useState('');
  // 由url参数与游戏是否有pc console游戏类型共同决定初始平台数据源与默认值
  const composedPlatformCur = useMemo(() => {
    return platformSourceListCur?.[0]?.value;
  }, [platformSourceListCur]);

  const [composedPlatformSelected, setComposedPlatformSelected] =
    useState(composedPlatformCur);

  // 根据商店数据源选择，返回对应榜单列
  const storerankMetricsListCur = useMemo(() => {
    const metricObjList =
      STORERANK_METRICS_OPTIONS_LIST?.filter(
        (item: any) => item.dataSource === composedPlatformSelected,
      ) || [];
    return metricObjList;
  }, [composedPlatformSelected]);

  // 根据是否已上线，确定展示steam榜单
  const metricSelectedDefaultObjCur = useMemo(() => {
    if (composedPlatformSelected === 'steam' && !isRelease) {
      const wishlistsObj = storerankMetricsListCur?.find(
        (item: any) => item.value === 'steam top wish lists',
      );
      return wishlistsObj;
    }
    return storerankMetricsListCur[0];
  }, [storerankMetricsListCur, isRelease, composedPlatformSelected]);

  // 分页segment--rank/market
  const [activeSegment, setActiveSegment] = useState(activeSegmentUrlCur);
  // 可视化榜单切换选项值
  const [metricSelectedValue, setMetricSelectedValue] = useState(
    metricSelectedDefaultObjCur?.value,
  );
  const [radioType, setRadioType] = useState('dod');

  const { filterLoading, regionOptions } = useMarketFilterApi({
    filterType: MarketSpecailType.common,
  });

  /** 获取Console国家筛选器选项*/
  const { isLoading: isRegionsOptionsLoading, regionsOptionsMap } =
    useStoreTopRegionOptionsMap(
      composedPlatformCur,
      {
        entityType,
        platforms: COMBINE_PLATFORMS,
      },
      true,
    );

  // 根据当前选择商店数据源与国家地区对应map确定当前多选国家筛选器选项数据
  const composedCountrySelectOptionsCur = useMemo(() => {
    if (!CONSOLE_PLATFORMS.includes(composedPlatformCur))
      return regionsOptionsMap.playstation;
    return regionsOptionsMap[composedPlatformCur];
  }, [regionsOptionsMap, composedPlatformCur]);

  // 根据当前选择商店数据源与国家地区对应map确定当前多选国家筛选器默认值
  const composedCountrySelectValueCur = useMemo(() => {
    return {
      type: 'multiple',
      countries: getCountriesValues(composedCountrySelectOptionsCur),
    };
  }, [composedCountrySelectOptionsCur]);

  // 根据当前已选子榜单，得到榜单数组value
  const storerankMetricsListValueCur = useMemo(() => {
    return storerankMetricsListCur?.map((item: any) => item.value) || [];
  }, [storerankMetricsListCur]);

  const defaultFilterValueCur = {
    ...defaultFilterValue,
    [filterKeyMap.COMPOSED_SELECT]: {
      value: {
        parent: composedPlatformSelected,
        child:
          storerankMetricsListValueCur.length > 0
            ? storerankMetricsListValueCur[0]
            : '',
      },
    },
    [filterKeyMap.STORERANK_SEGEMENT]: {
      value: activeSegment,
    },
    [filterKeyMap.COMPOSED_COUNTRY_SELECT]: {
      value: composedCountrySelectValueCur, // composedCountrySelectValueCur
    },
  };
  /** 筛选器value数据 **/
  const [filterValue, setFilterValue] = useState<any>(defaultFilterValueCur);
  useDeepCompareEffect(() => {
    setFilterValue(defaultFilterValueCur);
  }, [defaultFilterValueCur]);

  const filterCfg = {
    filterKeyList,
    regionFilterParams: {},
    defaultValue: defaultFilterValue,
    resetFilterValue: {
      ...defaultFilterValue,
    },
    showConfirmButton: false,
    isCustomLayoutFilter: true,
    specialParams: {
      defalutMarket: ['global'], // 没取到交集用的默认值
      disableNoChina: true,
      entityType,
      isOnlyExtendDate: true,
    },
  };

  const { updateEarliestDateData, baseReleaseTimeValue, earliestDayData } =
    useEarliestTime({
      entityType,
      basicInfo,
      topchartsState,
      segmentValue: activeSegment,
      setEarliestPeriodCur,
    });
  const getRealRegions = (params: any) => {
    const regionsValue = params?.regions;
    if (params?.platform === 'steam') {
      if (
        Array.isArray(regionsValue) &&
        regionsValue?.length === 1 &&
        regionsValue[0] === 'global'
      ) {
        return [
          ...(params?.segement === 'market' ? [] : regionsValue),
          ...getCountriesValues(regionsOptionsMap?.steam),
        ];
      }
      if (
        Array.isArray(regionsValue) &&
        regionsValue?.length === 1 &&
        regionsValue[0] === 'global_wo_china'
      ) {
        return [...getCountriesValues(regionsOptionsMap?.steam)]?.filter(
          (item) => item !== 'cn',
        );
      }
      return regionsValue;
    }
    if (params?.platform === 'epic') {
      return ['global'];
    }
    return params?.regions;
  };

  // 根据选择数据源平台，返回对应游戏类型与ID
  const getGameIdCur = (params: any) => {
    let paramsTemp = params;

    if (CONSOLE_PLATFORMS?.includes(params?.platform)) {
      const consoleGame = combineGameIdList?.find(
        (item: any) => item.entity_type === 'console',
      );
      if (consoleGame) {
        paramsTemp = {
          ...paramsTemp,
          app_ids: consoleGame?.id !== '' ? [consoleGame?.id] : [],
          entity_type: 'console',
        };
      }
    } else {
      const pcGame = combineGameIdList?.find(
        (item: any) => item.entity_type === 'pc',
      );
      if (pcGame) {
        paramsTemp = {
          ...paramsTemp,
          app_ids: pcGame?.id !== '' ? [pcGame?.id] : [],
          entity_type: 'pc',
        };
      }
    }

    // 是否有变化segment
    if (params?.segement !== activeSegment) {
      setActiveSegment(params?.segement);

      const pageLogConfig =
        params?.segement === 'market'
          ? dbiPageLogMes?.pcConsoleGameDetailsStoreRankMarketCount
          : dbiPageLogMes?.pcConsoleGameDetailsStoreRankRankTrend;
      if (pageLogConfig?.pageId) {
        buriedPointGame(pageLogConfig, gameId, 'edition_id');
      }
    }

    // 是否有变化platform
    if (params?.platform !== composedPlatformSelected) {
      setComposedPlatformSelected(params?.platform);
      const metricObjList =
        STORERANK_METRICS_OPTIONS_LIST?.filter(
          (item: any) => item.dataSource === params?.platform,
        ) || [];
      setMetricSelectedValue(metricObjList[0]?.value);
      paramsTemp = {
        ...paramsTemp,
        order_source: metricObjList[0]?.value,
      };
    }

    // 是否有变化time
    if (
      fetchParams?.start_date !== paramsTemp?.start_date ||
      fetchParams?.end_date !== paramsTemp?.end_date ||
      paramsTemp?.platform === 'epic'
    ) {
      setIsOperateTime(true);
    }

    return {
      ...paramsTemp,
      regions: getRealRegions(params),
      isNoNeedTrendRegions: true,
    };
  };

  const getDownLoadAppids = () => {
    const idList: any = [];
    combineGameIdList?.forEach((item: any) => {
      if (
        item?.id &&
        item?.id !== '' &&
        ['pc', 'console'].includes(item.entity_type)
      ) {
        idList.push(item?.id);
      }
    });
    return idList;
  };

  const {
    fetchParams,
    tableTotal,
    tableData,
    setTableData,
    lineData,
    trendLoading,
    trendError,
    getTableData,
    defaultSelectionGames,
    tableSelection,
    getLineChartData,
    setTableSelection,
    tableLoading,
    updateSelection,
    tableSearchValue,
    setTableSearchValue,
    setIsOperateCheckbox,
    reqDateObject,
    cacheTableData,
    setBoolean,
    setPageSortAttr,
    pageSortAttr,
    tableDataMarket,
  } = useRankTrendModel({
    getTableApiFun: getCombieStoreRanksDetailsRankTrendData,
    getTrendApiFun: fetchStoreRanksDetailsTrendData,
    entityType,
    marketsValue: composedCountrySelectValueCur?.countries,
    rankType,
    updateEarliestDateData,
    storerankMetricsListValueCur,
    defaultParams,
    metricSelectedDefaultObjCur,
  });
  const firstWeekSinceReleaseDates = useMemo(() => {
    if (!isFirstWeekSinceReleaseShortcut(topchartsState?.selectedTimeShortcutType)) {
      return [];
    }
    return getFirstWeekSinceReleaseDates({
      dateType: fetchParams?.granularity,
      startDate: fetchParams?.start_date,
      endDate: fetchParams?.end_date,
      releaseDate: baseReleaseTimeValue,
    });
  }, [
    topchartsState?.selectedTimeShortcutType,
    fetchParams?.granularity,
    fetchParams?.start_date,
    fetchParams?.end_date,
    baseReleaseTimeValue,
  ]);

  /**
   * 筛选器统一值处理后，confirm回调
   */
  const fetchFilterChange = (payload: any) => {
    // console.log('handleFilterChange-payload', payload);
    // 设置setComposedPlatformSelected
    const params = handleFilterToApiParams(fetchParams, payload);
    // console.log('handleFilterChange-params', params);
    getTableData(getGameIdCur(params));
  };

  const downloadRegions = useMemo(() => {
    if (Array.isArray(tableData) && tableData?.length > 0) {
      return (
        tableData
          ?.filter(
            (item: any) =>
              Array.isArray(item?.metrics) && item?.metrics?.length > 0,
          )
          ?.map((item: any) => item?.market) || []
      );
    }
    return fetchParams?.regions;
  }, [tableData]);

  const playstationMarket = useMemo(() => {
    const playstation = get(regionsOptionsMap, 'playstation', []);
    if (Array.isArray(playstation)) {
      const list = playstation?.reduce((result: any, item) => {
        const countrySet = item?.country_set || [];
        countrySet?.forEach((config: any) => {
          result.push(config?.country_code);
        });
        return result;
      }, []);
      return list;
    }
    return [];
  }, [regionsOptionsMap]);

  const steamMarket = useMemo(() => {
    if (Array.isArray(regionOptions)) {
      const list = regionOptions?.reduce((result, item) => {
        const countrySet = item?.country_set || [];
        countrySet?.forEach((config: any) => {
          result.push(config?.country_code);
        });
        return result;
      }, []);

      return list;
    }
    return [];
  }, [regionOptions]);

  const combineMarkets = useMemo(() => {
    if (Array.isArray(regionOptions)) {
      const list = regionOptions?.reduce((result, item) => {
        const countrySet = item?.country_set || [];
        countrySet?.forEach((config: any) => {
          result.push({
            enName: config?.country_code,
            name: config?.country_name_en,
          });
        });
        return result;
      }, []);

      return list;
    }
    return [];
  }, [regionOptions]);

  const downloadTableParams = useMemo(
    () => ({
      name: basicInfo?.name,
      regionOptions,
      date_type: fetchParams?.granularity,
      regions_all: downloadRegions,
      regions_new: fetchParams?.regions,
      current_tab: rankType === 'avg_ranking' ? 'avg' : 'latest',
      hasPc: true,
      hasConsole: true,
      markets: [],
      playstation_market: playstationMarket,
      steam_market: steamMarket,
      combine_id: basicInfo?.id,
      tableTotal,
      ...fetchParams,
      app_ids: getDownLoadAppids(),
      storeRankPlatform: filterValue?.composed_select?.value.parent,
    }),
    [
      fetchParams,
      tableData,
      basicInfo,
      rankType,
      regionOptions,
      tableTotal,
      combineGameIdList,
      filterValue,
    ],
  );

  // 根据商店数据源选择，返回对应榜单列
  const allLoading = useMemo(() => {
    return filterLoading || isBasicLoading || isRegionsOptionsLoading;
  }, [filterLoading, isBasicLoading, isRegionsOptionsLoading]);

  // filter公共hooks，暴露全局统一处理filterValue方法，再调接口
  const { updateFilterDatas } = useInitFilterHook({
    entityType,
    filterCfg,
    filterValue,
    setFilterValue,
    filterConfirm: fetchFilterChange,
    isBasicLoading: allLoading,
    defaultInputList,
    isOperateTime,
  });

  useEffect(() => {
    const pageLogConfig =
      activeSegmentUrlCur === 'market'
        ? dbiPageLogMes?.pcConsoleGameDetailsStoreRankMarketCount
        : dbiPageLogMes?.pcConsoleGameDetailsStoreRankRankTrend;
    if (!pageLogConfig?.pageId) return;
    buriedPointGame(pageLogConfig, gameId, 'edition_id');
  }, [gameId]);

  return (
    <div className="dbi-mobile-games-storerank dbi-mobile-games-details-store-rank">
      <div className="dbi-statsfilter-warp">
        <div className="statsfilter-warp-left">
          {/* RankTrend筛选器内容 */}
          <CommonFilter
            timeFilterValues={{
              type: fetchParams.granularity,
              value: [fetchParams.start_date, fetchParams.end_date],
            }}
            filterConfirm={updateFilterDatas}
            filterValue={filterValue}
            setFilterValue={setFilterValue}
            filterCfg={filterCfg}
            defaultInputList={defaultInputList}
            isBasicLoading={filterLoading}
            platformSourceListCur={platformSourceListCur}
            regionOptions={regionsOptionsMap?.steam}
            getRadioOptionsTips={getRadioOptionsTips}
            composedCountryOptionsMap={regionsOptionsMap}
            sinceReleaseTime={baseReleaseTimeValue}
            earliestDataTime={earliestPeriodCur}
          />
        </div>
        {/* 下载内容 */}
        <div className="dbi-statsfilter-warp-right">
          {activeSegment === 'rank' ? (
            <ExportTable
              entityType={entityType}
              tableParams={downloadTableParams}
              downloadLogConfigKey="pcConsoleGameDetailsStoreRankDownloadExcel"
              isShowMaxDownloadRowsTips={false}
              businessType="pc-console-detail-store-rank"
              downloadModalSize="l"
              isDisableDownload={tableLoading}
              isDisableDataAmount={true}
              total={tableTotal}
              newStyleFileName={`${basicInfo?.name}_Countries(${tableTotal}) Store Rank`}
              useWebDownload={true}
              downloadKey="pcConsoleRankTable"
            />
          ) : (
            <ExportTable
              entityType={entityType}
              tableParams={downloadTableParams}
              downloadLogConfigKey="TopChartsPCAndConsoleGamesStoreRankMarketCountDownloadButton"
              isShowMaxDownloadRowsTips={false}
              businessType="pc-console-detail-store-rank-market-count"
              downloadModalSize="l"
              isDisableDownload={tableLoading}
              isDisableDataAmount={true}
              total={tableTotal}
              newStyleFileName={`${basicInfo?.name}_Countries(${tableTotal})`}
              useWebDownload={true}
              downloadKey="pcConsoleMarketCount"
            />
          )}
        </div>
      </div>

      {activeSegment === 'rank' ? (
        <RankTrend
          {...props}
          pageId={`details-storerank—${activeSegment}`}
          loading={loading}
          setLoading={setLoading}
          entityType={entityType}
          setActiveSegment={setActiveSegment}
          activeSegment={activeSegment}
          filterLoading={filterLoading}
          isRegionsOptionsLoading={isRegionsOptionsLoading}
          regionOptions={regionOptions}
          basicInfo={basicInfo}
          gameId={gameId}
          isBasicLoading={isBasicLoading}
          platformSourceListCur={platformSourceListCur}
          regionsOptionsMap={regionsOptionsMap}
          composedPlatformCur={composedPlatformCur}
          steamFilterLoading={isRegionsOptionsLoading}
          steamRegionOptions={regionsOptionsMap?.steam}
          storerankMetricsListCur={storerankMetricsListCur}
          earliestDayData={earliestDayData}
          setEarliestPeriodCur={setEarliestPeriodCur}
          allLoading={allLoading}
          composedCountrySelectValueCur={composedCountrySelectValueCur}
          fetchParams={fetchParams}
          metricSelectedValue={metricSelectedValue}
          setMetricSelectedValue={setMetricSelectedValue}
          lineData={lineData}
          trendLoading={trendLoading}
          trendError={trendError}
          tableSelection={tableSelection}
          tableData={tableData}
          setTableData={setTableData}
          setTableSelection={setTableSelection}
          getTableData={getTableData}
          getLineChartData={getLineChartData}
          defaultSelectionGames={defaultSelectionGames}
          tableSearchValue={tableSearchValue}
          tableLoading={tableLoading}
          setIsOperateCheckbox={setIsOperateCheckbox}
          rankType={rankType}
          setRankType={setRankType}
          reqDateObject={reqDateObject}
          setBoolean={setBoolean}
          tableTotal={tableTotal}
          setTableSearchValue={setTableSearchValue}
          cacheTableData={cacheTableData}
          updateSelection={updateSelection}
          setPageSortAttr={setPageSortAttr}
          pageSortAttr={pageSortAttr}
          radioType={radioType}
          setRadioType={setRadioType}
          combineMarkets={combineMarkets}
          firstWeekSinceReleaseDates={firstWeekSinceReleaseDates}
        />
      ) : (
        <MarketCount
          {...props}
          pageId={`details-storerank—${activeSegment}`}
          allLoading={allLoading}
          setLoading={setLoading}
          entityType={entityType}
          setActiveSegment={setActiveSegment}
          activeSegment={activeSegment}
          filterLoading={filterLoading}
          isRegionsOptionsLoading={isRegionsOptionsLoading}
          regionOptions={regionOptions}
          basicInfo={basicInfo}
          gameId={gameId}
          platformSourceListCur={platformSourceListCur}
          regionsOptionsMap={regionsOptionsMap}
          composedPlatformCur={composedPlatformCur}
          fetchParams={fetchParams}
          tableDataMarket={tableDataMarket}
          trendLoading={trendLoading}
          firstWeekSinceReleaseDates={firstWeekSinceReleaseDates}
        />
      )}
    </div>
  );
};

export default StoreRankDetails;
